/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useKeystoneSession } from '../../../auth/use-keystone-session';
import { useCallback, useContext, useEffect } from 'react';
import { ComponentController as BpComponentController, DisplayMode, RtaBlueprintComponent } from '@rta/rta-blueprint-component';
import { BlueprintInstanceType, type EventMessage, EventPayloadAction, useSubscription } from '@rta/rta-blueprint-library';
import { Box, Image, useColorModeValue } from '@chakra-ui/react';
import { Kitchen3d, Kitchen3dController as TdComponentController, RENDERING_CAPTURE_ASPECT } from '@rta/rta-blueprint-3d-component';

import { AppContext } from '@context';
import { FindCustomer } from './find-customer';
import { getDesignImage } from '@helpers';
import { lang } from '@lang';
import { NewSubmissionPanel } from './new-submission/new-submission-panel';
import { useAutoSaveFeature } from '@features';
import { useAnnotationPresets, useDesign, useSaveOnBlueprintExport, useThreeDInteractions } from '../../../hooks';
import { appMode, ContentPanelMode, ContextPanelMode, currentUserState, projectState, pushContextPanel, selectContentPanelMode, selectCustomerName, selectIsBootstrapped, selectNavigation, setContentPanelMode, useAppDispatch, useAppSelector } from '@state';
import { DesignErrorBoundary } from '../../ui/design-error-boundary';
import { ThreeDContextMenu } from './three-d-context-menu';
import { WebGLErrorBoundary } from '../../ui/webgl-error-boundary';

import bpBackground from '@assets/blueprint-drawing-bg.png';

// Thumbnail autozoom derives from this container, so it must match the capture aspect
const THUMBNAIL_RENDER_WIDTH = 480;
const THUMBNAIL_RENDER_HEIGHT = Math.round(THUMBNAIL_RENDER_WIDTH / RENDERING_CAPTURE_ASPECT);

export const ContentPanel = () => {
	// const start = performance.now();
	const { status } = useKeystoneSession();
	const isAuthenticated = status === 'ready';
	const dispatch = useAppDispatch();
	const isBootstrapped = useAppSelector(selectIsBootstrapped);
	const contentModeState = useAppSelector(selectContentPanelMode);
	const appNav = useAppSelector(selectNavigation);

	const { appState: { customerDesigns } } = useContext(AppContext);
	const [controller, design] = useDesign<BpComponentController | TdComponentController>();
	const { annotationPresets, onSaveAnnotationPreset, onDeleteAnnotationPreset } = useAnnotationPresets();
	const onBlueprintExported = useSaveOnBlueprintExport();
	// const kitchen = blueprint?.kitchens.getLatestVersion() ?? null;
	// const bpController: BpComponentController | null = (customerDesigns.designs.find((d) => d.blueprint.id === blueprint?.id)?.bpController as BpComponentController) ?? null;

	useAutoSaveFeature();
	useThreeDInteractions();

	const designerBg = useColorModeValue('white', '#303030');
	const customerName = useAppSelector(selectCustomerName);
	const currentUser = useAppSelector(currentUserState);
	const project = useAppSelector(projectState);

	useEffect(() => {
		if (controller instanceof BpComponentController) {
			controller.options.customerName = customerName;
			controller.kitchen?.setDesignDetailCustomerName(customerName);
		}
	}, [controller, customerName]);

	useEffect(() => {
		if (!(controller instanceof BpComponentController)) return;
		const { designer } = project[0] ?? {};
		// a deal whose HubSpot owner left the company keeps blank names, so it must read 'Unassigned' rather than borrow the signed-in user
		const nameSource = designer ?? currentUser;
		const firstName = nameSource?.firstName ?? '';
		const lastName = nameSource?.lastName ?? '';
		const fullName = `${firstName} ${lastName}`.trim();
		const initials = [firstName, lastName]
			.filter((n) => n.length > 0)
			.map((n) => n[0].toUpperCase())
			.join('');
		const currentUserInitials = [currentUser?.firstName ?? '', currentUser?.lastName ?? '']
			.filter((n) => n.length > 0)
			.map((n) => n[0].toUpperCase())
			.join('');
		controller.options.designerName = fullName || lang.crm.text.designerUnassigned;
		controller.options.designerInitials = initials;
		controller.options.currentUserInitials = currentUserInitials;
	}, [controller, project, currentUser]);

	useEffect(() => {
		if (!(controller instanceof BpComponentController)) return;
		const layoutDescription = controller.kitchen?.description ?? '';
		controller.options.layoutDescription = layoutDescription;
	}, [controller, controller?.kitchen?.description]);

	useEffect(() => {
		if (!(controller instanceof BpComponentController)) return;
		// Prefer the design's saved image (inline thumbnail, legacy rendering fallback) — no hidden 3D render/screenshot needed
		const savedRendering = getDesignImage(design?.blueprint);
		if (savedRendering) {
			controller.options.threeDImageUrl = savedRendering;
			return;
		}
		const tdController = design?.tdController ?? null;
		if (!tdController) return;
		const updateThreeDImage = (): void => {
			const image = tdController.getImage();
			if (image && image !== '') {
				controller.options.threeDImageUrl = image;
			}
		};
		updateThreeDImage();
		// Poll for 3D image if not immediately available (scene may not have rendered yet)
		const interval = setInterval(() => {
			if (controller.options.threeDImageUrl && controller.options.threeDImageUrl !== '') {
				clearInterval(interval);
				return;
			}
			updateThreeDImage();
		}, 2000);
		return () => clearInterval(interval);
	}, [controller, design?.tdController, design?.blueprint?.thumbnail]);

	let contentMode = isAuthenticated ? contentModeState : ContentPanelMode.Anonymous;
	if (!isBootstrapped) contentMode = ContentPanelMode.Loading;

	const [uiEvent] = useSubscription(controller, false, [['ui', 'double-click']]);
	// addUiSub('ui', 'click');

	const [event] = useSubscription(controller, false, [
		['*', 'show'],
		['*', 'requested'],
		['navigation-display-mode', 'updated'],
		['kitchen_version', 'updated']
	]);

	useEffect(() => {
		if (uiEvent && uiEvent.payload.ref) {
			const refMap = controller?.kitchen?.getReferenceMap(uiEvent.payload.ref as string);
			if (uiEvent.noun === 'ui' && refMap) {
				const toggleMode = contentModeState === ContentPanelMode.Designer ? ContentPanelMode.ThreeD : ContentPanelMode.Designer;

				switch (uiEvent.action) {
					case 'click':
						switch (refMap.type) {
							case BlueprintInstanceType.Countertop:
								dispatch(pushContextPanel({ mode: ContextPanelMode.CountertopProperties, id: uiEvent.payload.ref as string, temporary: true	}));
								break;
							case BlueprintInstanceType.Island:
								dispatch(pushContextPanel({ mode: ContextPanelMode.IslandProperties, id: uiEvent.payload.ref as string, temporary: true	}));
								break;
							case BlueprintInstanceType.Connection:
								dispatch(pushContextPanel({ mode: ContextPanelMode.ConnectionProperties, id: uiEvent.payload.ref as string, temporary: true	}));
								break;
							case BlueprintInstanceType.Obstruction:
								dispatch(pushContextPanel({ mode: ContextPanelMode.ObstructionProperties, id: uiEvent.payload.ref as string, temporary: true	}));
								break;
							case BlueprintInstanceType.PanelItem:
								// eslint-disable-next-line max-depth
								if (refMap.panelItem?.item.category.key === 'cutout') {
									dispatch(pushContextPanel({ mode: ContextPanelMode.CutoutProperties, id: uiEvent.payload.ref as string, temporary: true	}));
								} else if (refMap.panelItem?.item.category?.key === 'spacer') {
									dispatch(pushContextPanel({ mode: ContextPanelMode.SpacerProperties, id: uiEvent.payload.ref as string, temporary: true	}));
								} else {
									dispatch(pushContextPanel({ mode: ContextPanelMode.ApplianceProperties, id: uiEvent.payload.ref as string, temporary: true	}));
								}
								break;
							default:
						}
						break;
					case 'double-click':
						if (design?.bpController && refMap && refMap.island) {
							design.bpController.navigation.islandId = refMap.island.id;
							design.bpController.navigation.panelId = refMap.panel ? refMap.panel.id : refMap.island.frontPanel.id;
							design.bpController.navigation.displayMode = DisplayMode.Panel;
							// dispatch(setContentPanelMode(ContentPanelMode.Designer));
						}

						dispatch(setContentPanelMode(toggleMode));
						break;
					default:
				}
			}
		}
	}, [uiEvent]);

	// The 3D context menu runs its actions on the design's blueprint controller, so while the 3D
	// view owns `controller` those show/requested events would otherwise go unheard.
	const [menuEvent] = useSubscription(
		contentModeState === ContentPanelMode.ThreeD ? design?.bpController ?? null : null,
		false,
		[['*', 'show'], ['*', 'requested']]
	);

	const openPanelForEvent = useCallback((event: EventMessage | null): void => {
		if (!event) return;

		const { ref, data } = event.payload as EventPayloadAction;

		if (event.action === 'show') {
			const contextPanel = event.noun.replace('_', '-');
			console.log('show:', contextPanel, event);
			if (Object.values(ContextPanelMode).includes(contextPanel as ContextPanelMode)) {
				dispatch(pushContextPanel({ mode: contextPanel as ContextPanelMode, id: ref as string, temporary: true	}));
			}
		}

		if (event.action === 'requested') {
			// deepcode ignore DuplicateCaseBody: <please specify a reason of ignoring this>
			switch (event.noun) {
				case 'add_appliance_action':
				case 'swap_appliance_action':
					console.log('add_appliance_action', data?.position);
					dispatch(pushContextPanel({
						mode: ContextPanelMode.AddAppliance,
						id: ref as string,
						temporary: true,
						data: {
							position: data?.position ?? {}
						}
					}));
					break;
				case 'add_cutout_action':
					dispatch(pushContextPanel({
						mode: ContextPanelMode.AddCutout,
						id: ref as string,
						temporary: true,
						data: {
							position: data?.position ?? {},
							countertopCutout: data?.countertopCutout ?? false
						}
					}));
					break;
				case 'add_spacer_action':
					dispatch(pushContextPanel({
						mode: ContextPanelMode.AddSpacer,
						id: ref as string,
						temporary: true,
						data: {
							position: data?.position ?? {},
							isCountertop: data?.isCountertop ?? false
						}
					}));
					break;
				case 'add_custom_appliance_action':
					dispatch(pushContextPanel({
						mode: ContextPanelMode.AddCustomAppliance,
						id: ref as string,
						temporary: true,
						data: {
							position: data?.position ?? {}
						}
					}));
					break;
				default:
			}
		}
	}, [dispatch]);

	useEffect(() => { openPanelForEvent(event as EventMessage | null); }, [event]);
	useEffect(() => { openPanelForEvent(menuEvent as EventMessage | null); }, [menuEvent]);

	// useEffect(() => {
	// 	const end = performance.now();
	// 	console.log(`render-time:ContentPanel: ${end - start} ms`);
	// });

	let content = null;
	// view mode always uses 'three-d'
	switch (appNav.appMode === appMode.view ? 'three-d' : contentMode) {
		case 'anonymous':
		case 'loading':
			content = <Image key="background-image" src={bpBackground} alt="Background Image" minW="-webkit-fill-available" minH="fit-content" position="absolute" top="-100px" opacity={0.6}/>;
			// content = null;
			break;
		case 'designer':
			if (controller instanceof BpComponentController) {
				content = <DesignErrorBoundary key={`error-boundary-${controller.blueprint!.id}-${controller.kitchenVersion ?? 'draft'}`}>
					<RtaBlueprintComponent
						key={`blueprint-${controller.blueprint!.id}-${controller.kitchenVersion ?? 'draft'}`}
						controller={controller as BpComponentController}
						backgroundColor={designerBg}
						annotationPresets={annotationPresets}
						onSaveAnnotationPreset={onSaveAnnotationPreset}
						onDeleteAnnotationPreset={onDeleteAnnotationPreset}
						onBlueprintExported={onBlueprintExported}
					/>
					{/* Hidden 3D renderer — only for blueprint-view threeDImageUrl capture, and only
					   when there is NO saved rendering to use (otherwise we'd needlessly render 3D).
					   Sized to the capture aspect so its autozoom matches every other rendering path. */}
					{design?.tdController && !getDesignImage(design?.blueprint) && (controller as BpComponentController).navigation?.displayMode === DisplayMode.BlueprintView && (
						<Box
							position="absolute"
							width={`${THUMBNAIL_RENDER_WIDTH}px`}
							height={`${THUMBNAIL_RENDER_HEIGHT}px`}
							overflow="hidden"
							opacity={0}
							pointerEvents="none"
							left="-9999px"
						>
							<Kitchen3d
								controller={design.tdController as TdComponentController}
								backgroundColor={'#ffffff'}
							/>
						</Box>
					)}
				</DesignErrorBoundary>;
			} else if (appNav.email && !customerDesigns.isLoading && customerDesigns.hasLoaded && customerDesigns.designs.length === 0) {
				content = <NewSubmissionPanel />;
			}
			break;
		case 'three-d':
			content = (
				<WebGLErrorBoundary>
					<ThreeDContextMenu
						tdController={controller as TdComponentController}
						bpController={(design?.bpController as BpComponentController) ?? null}
					>
						<Kitchen3d
							controller={controller as TdComponentController}
							backgroundColor={'var(--chakra-colors-bg-canvas)'}
						/>
					</ThreeDContextMenu>
				</WebGLErrorBoundary>
			);
			break;
		case 'new-submission':
			content = <NewSubmissionPanel />;
			break;
		case 'find-customer':
			content = <FindCustomer/>;
			break;
		default:
			content = null;
	}

	return (<>
		<Box w={'100%'} h={'100%'} bgColor="gray.100">
			{content}
		</Box>
	</>);
};
